import { useState } from 'react';
import type { FormEvent } from 'react';
import type {
  Contact,
  ContactCreateBody,
  SourceContact,
  StatutPipeline,
  TerritoireContact,
  TypeClient,
} from '@cockpit/shared';
import { SourcesContact, StatutsPipeline, TerritoiresContact, TypesClient } from '@cockpit/shared';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import {
  LABEL_SOURCE_CONTACT,
  LABEL_STATUT_PIPELINE,
  LABEL_TERRITOIRE_CONTACT,
  LABEL_TYPE_CLIENT,
  isoToDateInput,
  parseDateToIso,
  parseEuroToCentimes,
} from '../../lib/format';
import { departementFromCP } from '../../lib/departement';

interface Props {
  initial?: Contact | null;
  submitting?: boolean;
  submitLabel?: string;
  onSubmit: (body: ContactCreateBody) => void;
  onCancel: () => void;
}

export function ContactForm({
  initial,
  submitting,
  submitLabel = 'Créer le contact',
  onSubmit,
  onCancel,
}: Props): JSX.Element {
  const [nom, setNom] = useState(initial?.nom ?? '');
  const [prenom, setPrenom] = useState(initial?.prenom ?? '');
  const [societe, setSociete] = useState(initial?.societe ?? '');
  const [typeClient, setTypeClient] = useState<TypeClient>(initial?.type_client ?? 'Paysagiste');
  const [telephone, setTelephone] = useState(initial?.telephone ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [adresse, setAdresse] = useState(initial?.adresse ?? '');
  const [codePostal, setCodePostal] = useState(initial?.code_postal ?? '');
  const [ville, setVille] = useState(initial?.ville ?? '');
  const [siret, setSiret] = useState(initial?.siret ?? '');
  const [tvaIntra, setTvaIntra] = useState(initial?.tva_intracom ?? '');
  const [territoire, setTerritoire] = useState<TerritoireContact>(
    initial?.territoire ?? 'Les_deux',
  );
  const [source, setSource] = useState<SourceContact>(initial?.source ?? 'Autre');
  const [statut, setStatut] = useState<StatutPipeline>(initial?.statut_pipeline ?? 'Prospect');
  const [datePremierContact, setDatePremierContact] = useState(
    isoToDateInput(initial?.date_premier_contact ?? null),
  );
  const [prochaineRelance, setProchaineRelance] = useState(
    isoToDateInput(initial?.prochaine_relance ?? null),
  );
  const [valeurEstimeeEur, setValeurEstimeeEur] = useState(
    initial !== null && initial !== undefined && initial.valeur_estimee_centimes > 0
      ? (initial.valeur_estimee_centimes / 100).toString().replace('.', ',')
      : '',
  );
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const departementCalc = departementFromCP(codePostal) ?? '—';

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setError(null);
    if (nom.trim() === '') {
      setError('Le nom est obligatoire.');
      return;
    }
    const valeur =
      valeurEstimeeEur.trim() === '' ? 0 : (parseEuroToCentimes(valeurEstimeeEur) ?? 0);

    const body: ContactCreateBody = {
      nom: nom.trim(),
      prenom: prenom.trim() === '' ? null : prenom.trim(),
      societe: societe.trim() === '' ? null : societe.trim(),
      type_client: typeClient,
      telephone: telephone.trim() === '' ? null : telephone.trim(),
      email: email.trim() === '' ? null : email.trim(),
      adresse: adresse.trim() === '' ? null : adresse.trim(),
      code_postal: codePostal.trim() === '' ? null : codePostal.trim(),
      ville: ville.trim() === '' ? null : ville.trim(),
      siret: siret.trim() === '' ? null : siret.trim(),
      tva_intracom: tvaIntra.trim() === '' ? null : tvaIntra.trim(),
      territoire,
      source,
      statut_pipeline: statut,
      date_premier_contact: datePremierContact === '' ? null : parseDateToIso(datePremierContact),
      prochaine_relance: prochaineRelance === '' ? null : parseDateToIso(prochaineRelance),
      valeur_estimee_centimes: valeur,
      notes: notes.trim() === '' ? null : notes.trim(),
    };
    onSubmit(body);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {error !== null && (
        <div className="text-xs text-red-300 bg-red-900/20 border border-red-900/50 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input label="Nom *" value={nom} onChange={(e): void => setNom(e.target.value)} required />
        <Input label="Prénom" value={prenom} onChange={(e): void => setPrenom(e.target.value)} />
        <Input label="Société" value={societe} onChange={(e): void => setSociete(e.target.value)} />
        <Select
          label="Type de client"
          value={typeClient}
          onChange={(e): void => setTypeClient(e.target.value as TypeClient)}
          options={TypesClient.map((t) => ({ value: t, label: LABEL_TYPE_CLIENT[t] ?? t }))}
        />
        <Select
          label="Territoire"
          value={territoire}
          onChange={(e): void => setTerritoire(e.target.value as TerritoireContact)}
          options={TerritoiresContact.map((t) => ({
            value: t,
            label: LABEL_TERRITOIRE_CONTACT[t] ?? t,
          }))}
        />
        <Select
          label="Statut pipeline"
          value={statut}
          onChange={(e): void => setStatut(e.target.value as StatutPipeline)}
          options={StatutsPipeline.map((s) => ({ value: s, label: LABEL_STATUT_PIPELINE[s] ?? s }))}
        />
      </section>

      <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          label="Téléphone"
          value={telephone}
          onChange={(e): void => setTelephone(e.target.value)}
        />
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e): void => setEmail(e.target.value)}
        />
        <Select
          label="Source"
          value={source}
          onChange={(e): void => setSource(e.target.value as SourceContact)}
          options={SourcesContact.map((s) => ({ value: s, label: LABEL_SOURCE_CONTACT[s] ?? s }))}
        />
        <Input
          label="Adresse"
          value={adresse}
          onChange={(e): void => setAdresse(e.target.value)}
          className="md:col-span-2"
        />
        <Input
          label="Code postal"
          value={codePostal}
          onChange={(e): void => setCodePostal(e.target.value)}
          hint={`Département déduit : ${departementCalc}`}
        />
        <Input label="Ville" value={ville} onChange={(e): void => setVille(e.target.value)} />
      </section>

      <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input label="SIRET" value={siret} onChange={(e): void => setSiret(e.target.value)} />
        <Input
          label="TVA intracom"
          value={tvaIntra}
          onChange={(e): void => setTvaIntra(e.target.value)}
        />
        <Input
          label="Valeur estimée (€ HT)"
          value={valeurEstimeeEur}
          onChange={(e): void => setValeurEstimeeEur(e.target.value)}
          placeholder="Pour le pipeline Kanban"
        />
        <Input
          label="Premier contact"
          type="date"
          value={datePremierContact}
          onChange={(e): void => setDatePremierContact(e.target.value)}
        />
        <Input
          label="Prochaine relance"
          type="date"
          value={prochaineRelance}
          onChange={(e): void => setProchaineRelance(e.target.value)}
        />
      </section>

      <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
        <label className="text-xs font-medium text-slate-300">Notes</label>
        <textarea
          value={notes}
          onChange={(e): void => setNotes(e.target.value)}
          rows={4}
          className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 focus:border-floryon-green focus:outline-none focus:ring-1 focus:ring-floryon-green/50"
        />
      </section>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting === true ? 'Enregistrement…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}
